import { DestroyRef, inject, Injectable, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationError,
  NavigationCancel,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { environment } from '@environments/environment';
import { LoggerService } from './logger.service';
import { LCPPerformanceEntry, LayoutShiftEntry } from '../models/performance.model';
import { sanitizeUrl } from '../utils/url-sanitizer';

/**
 * True when a NavigationError's cause is a stale lazy-loaded chunk — the bundle
 * hash rotated after a deploy, so the dynamic import 404s/fails. Recoverable by
 * reloading to fetch the fresh index.html. Generic/transient navigation failures
 * (the empty `{}` payloads observed in ADMIN-INTERFACE-7B) return false and must
 * NOT trigger a reload.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  if ((error as { name?: unknown }).name === 'ChunkLoadError') return true;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== 'string') return false;
  return /loading chunk \S+ failed/i.test(message)
    || /failed to fetch dynamically imported module/i.test(message)
    || /error loading dynamically imported module/i.test(message)
    || /importing a module script failed/i.test(message);
}

/**
 * One reload per 30s window to recover a stale lazy-chunk without risking a reload
 * loop when the chunk is genuinely unreachable. Returns false (no reload) if
 * sessionStorage is unavailable (privacy mode) — a loop is worse than a logged warn.
 */
function shouldReloadForStaleChunk(): boolean {
  try {
    const KEY = 'pt:last-chunk-reload';
    const now = Date.now();
    const last = Number(sessionStorage.getItem(KEY) ?? '0');
    if (now - last < 30_000) return false;
    sessionStorage.setItem(KEY, String(now));
    return true;
  } catch {
    // sessionStorage blocked (privacy mode / sandboxed iframe): refuse to reload.
    return false;
  }
}

/**
 * Master switch for performance tracking, exposed as a DI token so tests can
 * override the value without mutating the global `environment` object — the
 * latter is a module-level singleton and inter-spec ordering can race.
 *
 * Default: reads `environment.monitoring.enablePerformanceTracking` at first
 * injection.
 */
export const PERFORMANCE_TRACKING_ENABLED = new InjectionToken<boolean>(
  'PERFORMANCE_TRACKING_ENABLED',
  {
    providedIn: 'root',
    factory: () => environment.monitoring.enablePerformanceTracking,
  },
);

/**
 * Sentry breadcrumb sink, exposed as a DI token so tests can supply a
 * `jasmine.createSpy` at the provider seam.
 *
 * `import * as Sentry from '@sentry/angular'` is compiled by the esbuild test
 * builder into a frozen ES-module-namespace object whose members are
 * non-writable getters, so `spyOn(Sentry, 'addBreadcrumb')` throws
 * "is not declared writable or has no setter" (confirmed 2026-04-29 — see
 * sentry.service.spec.ts). Routing the call through this token avoids spying on
 * the namespace; the default factory delegates to the real SDK so production
 * behaviour is unchanged.
 */
export const SENTRY_ADD_BREADCRUMB = new InjectionToken<
  typeof Sentry.addBreadcrumb
>('SENTRY_ADD_BREADCRUMB', {
  providedIn: 'root',
  factory: () => Sentry.addBreadcrumb,
});

/**
 * Performance tracking service for route navigation and Web Vitals (LCP, CLS,
 * resource timing for `<script>` loads).
 *
 * Lifecycle: declared `providedIn: 'root'`. The root injector lives for the
 * application lifetime, so `DestroyRef` fires only on app shutdown — i.e.
 * full-page navigation, HMR, or test teardown. The `takeUntilDestroyed` calls
 * are primarily a TestBed-cleanup safety net; in production the subscriptions
 * live for the entire SPA session, which is the same lifetime as the Router
 * itself, so there is no leak.
 *
 * Privacy: navigation URLs are passed through {@link sanitizeUrl} before
 * logging to avoid leaking OAuth codes, password-reset tokens, or magic-link
 * tokens that may appear as query parameters.
 *
 * Observability: LCP and CLS are recorded both as structured logs and as
 * Sentry breadcrumbs so they appear in error reports.
 *
 * All work is gated on the {@link PERFORMANCE_TRACKING_ENABLED} token.
 */
@Injectable({ providedIn: 'root' })
export class PerformanceTrackerService {
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly enabled = inject(PERFORMANCE_TRACKING_ENABLED);
  private readonly addBreadcrumb = inject(SENTRY_ADD_BREADCRUMB);
  private readonly navigationStartTime = new Map<string, number>();

  constructor() {
    if (!this.enabled) {
      return;
    }
    this.setupRouterTracking();
    this.measureLCP();
    this.measureCLS();
    this.observeResourceTiming();
    this.trackScriptFailures();
  }

  private setupRouterTracking(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationStart) => {
        // Overlapping NavigationStart for the same URL means a previous
        // navigation never resolved (pre-empted, no NavigationEnd/Error/Cancel
        // emitted). Overwriting silently loses the prior start time. Surface
        // it in logs so concurrent-nav anomalies are visible.
        if (this.navigationStartTime.has(event.url)) {
          this.logger.warn('PerformanceTracker: overlapping NavigationStart for same URL', {
            url: sanitizeUrl(event.url),
          });
        }
        this.navigationStartTime.set(event.url, performance.now());
        this.logger.debug('Navigation started', { url: sanitizeUrl(event.url) });
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        const startTime = this.navigationStartTime.get(event.url);
        if (startTime !== undefined) {
          const duration = performance.now() - startTime;
          this.logger.info('Navigation completed', {
            url: sanitizeUrl(event.url),
            duration: `${duration.toFixed(2)}ms`,
            urlAfterRedirects: sanitizeUrl(event.urlAfterRedirects),
          });
          this.navigationStartTime.delete(event.url);
        }
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationError) => {
        const startTime = this.navigationStartTime.get(event.url);
        const duration = startTime !== undefined ? performance.now() - startTime : undefined;
        this.navigationStartTime.delete(event.url);

        // ADMIN-INTERFACE-7B: a NavigationError is usually transient/benign — most
        // often a lazy-chunk hash that rotated after a deploy, or a transport blip
        // when the SPA was served from a stale origin (the observed payload was an
        // empty `{}`). Escalating every one to logger.error promoted them to genuine
        // Sentry Error issues. Recover the actionable case (a stale lazy-chunk) by
        // reloading for the fresh bundle; log everything else at warn (prod
        // LoggerService minLevel is 'error', so warn is dropped before the Sentry
        // external handler).
        if (isChunkLoadError(event.error) && shouldReloadForStaleChunk()) {
          this.logger.warn('Navigation failed: stale lazy-chunk, reloading for fresh bundle', {
            url: sanitizeUrl(event.url),
          });
          window.location.reload();
          return;
        }
        this.logger.warn('Navigation failed', {
          url: sanitizeUrl(event.url),
          duration: duration !== undefined ? `${duration.toFixed(2)}ms` : undefined,
          error: event.error,
        });
      });

    // Cancelled navigations (auth guards, canDeactivate, redirects) need
    // explicit cleanup so the Map cannot grow unbounded.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationCancel),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationCancel) => {
        this.navigationStartTime.delete(event.url);
      });
  }

  /**
   * Observes Largest Contentful Paint and emits both a structured log and a
   * Sentry breadcrumb so the value is visible in error reports.
   */
  private measureLCP(): void {
    if (!('PerformanceObserver' in window)) return;
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length === 0) return;
        const lastEntry = entries[entries.length - 1] as LCPPerformanceEntry;
        const valueMs = Number(lastEntry.startTime.toFixed(2));
        const element = lastEntry.element?.tagName ?? 'Unknown';

        this.logger.debug('Largest Contentful Paint measured', {
          value: `${valueMs}ms`,
          element,
        });
        this.addBreadcrumb({
          category: 'web-vital',
          type: 'info',
          message: 'LCP',
          level: 'info',
          data: { valueMs, element },
        });
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (error) {
      this.logger.warn('LCP measurement not supported', { error });
    }
  }

  /**
   * Observes Cumulative Layout Shift and emits both a structured log and a
   * Sentry breadcrumb. CLS is reported continuously; the breadcrumb captures
   * the running cumulative value at every observer batch.
   */
  private measureCLS(): void {
    if (!('PerformanceObserver' in window)) return;
    try {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const layoutEntry = entry as LayoutShiftEntry;
          if (!layoutEntry.hadRecentInput) {
            clsValue += layoutEntry.value;
          }
        }
        const value = Number(clsValue.toFixed(4));
        const rating: 'Good' | 'Needs Improvement' | 'Poor' =
          clsValue < 0.1 ? 'Good' : clsValue < 0.25 ? 'Needs Improvement' : 'Poor';
        this.logger.debug('Cumulative Layout Shift measured', {
          value: value.toFixed(4),
          rating,
        });
        this.addBreadcrumb({
          category: 'web-vital',
          type: 'info',
          message: 'CLS',
          level: 'info',
          data: { value, rating },
        });
      });
      observer.observe({ type: 'layout-shift', buffered: true });
    } catch (error) {
      this.logger.warn('CLS measurement not supported', { error });
    }
  }

  /**
   * Observes successful `<script>` resource loads via the standards-track
   * Performance Observer API.
   *
   * Replaces a prior implementation that monkey-patched
   * `HTMLHeadElement.prototype.appendChild`. The patch leaked across HMR
   * reloads, was non-idempotent (re-wrapped on every bootstrap), and could
   * interfere with third-party libraries that inject scripts. The Performance
   * Observer API has zero global side effects and is automatically scoped to
   * the document lifetime.
   */
  private observeResourceTiming(): void {
    if (!('PerformanceObserver' in window)) return;
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
          if (entry.initiatorType !== 'script') continue;
          // Same defence as trackScriptFailures: an authenticated 3rd-party
          // script URL may carry `?token=...` — sanitize before splitting.
          const safeName = sanitizeUrl(entry.name);
          const filenameWithQuery = safeName.split('/').pop() || 'unknown';
          const bundleName = filenameWithQuery.split('?')[0] || filenameWithQuery;
          this.logger.info('Bundle loaded', {
            bundleName,
            loadTime: `${entry.duration.toFixed(2)}ms`,
            transferSize: entry.transferSize,
          });
        }
      });
      observer.observe({ type: 'resource', buffered: true });
    } catch (error) {
      this.logger.warn('Resource timing not supported', { error });
    }
  }

  /**
   * Captures `<script>` load FAILURES, which `PerformanceResourceTiming`
   * does not surface (only successful loads emit resource entries). Uses a
   * single passive capture-phase listener — does not mutate any prototype.
   *
   * Bootstrap-timing limitation: this listener is registered when the
   * service constructor runs, which is during `provideAppInitializer`. Any
   * `<script>` load failure that occurred BEFORE Angular bootstrapped
   * (e.g. the main bundle itself failing to load) cannot be captured here
   * and is delivered via Sentry's pre-bootstrap browser-level integration
   * instead. Within-app failures (lazy-loaded chunks) are captured normally.
   */
  private trackScriptFailures(): void {
    const handler = (event: ErrorEvent | Event): void => {
      const target = event.target;
      if (!(target instanceof HTMLScriptElement) || !target.src) return;
      // A failing script can have `?token=...` in its src (3rd-party
      // analytics/SSO scripts often carry auth in query). Sanitize FIRST,
      // then extract the filename portion.
      const safeSrc = sanitizeUrl(target.src);
      const filenameWithQuery = safeSrc.split('/').pop() || 'unknown';
      const bundleName = filenameWithQuery.split('?')[0] || filenameWithQuery;
      this.logger.error('Bundle failed to load', { bundleName });
    };
    // `error` events do not bubble; capture phase is required.
    window.addEventListener('error', handler, true);
    this.destroyRef.onDestroy(() => window.removeEventListener('error', handler, true));
  }

}
